<template>
  <section class="page-stack">
    <article class="card page-hero-card raid-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Raid Progression</p>
          <h2>{{ selectedRaid.name || 'Raid progression' }}</h2>
          <p>Live guild standings, boss kills and progression pace for the selected Raider.io raid.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Expansion: <span>{{ expansionLabel }}</span></span>
        <span class="filter-pill">Raid: <span>{{ selectedRaid.name || 'Loading...' }}</span></span>
        <span class="filter-pill">Difficulty: <span>{{ filters.difficulty }}</span></span>
        <span class="filter-pill">Region: <span>{{ filters.region }}</span></span>
      </div>
      <div class="page-actions">
        <button class="btn-icon-outline" type="button" aria-label="Reset filters" title="Reset filters" @click="resetFilters">
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button class="btn-icon" type="button" aria-label="Filters" title="Filters" @click="openFilters">
          <i class="fa-solid fa-sliders"></i>
        </button>
        <button class="btn-icon-outline" type="button" aria-label="Refresh" title="Refresh" @click="loadData">
          <i class="fa-solid fa-rotate"></i>
        </button>
        <a class="btn-icon-outline" :href="leaderboardLink" target="_blank" rel="noreferrer" aria-label="Open Raider.io" title="Open Raider.io">
          <i class="fa-solid fa-arrow-up-right-from-square"></i>
        </a>
      </div>
    </div>

    <div
      v-if="isFiltersOpen"
      class="app-modal-backdrop filter-drawer-backdrop"
      :class="{ 'is-closing': closingFilters }"
      @click.self="closeFilters"
    >
      <aside class="app-filter-drawer" role="dialog" aria-modal="true" aria-labelledby="raid-filters-title">
        <header>
          <h2 id="raid-filters-title">Raid filters</h2>
          <p>Select raid, region and difficulty.</p>
        </header>
        <section>
          <form class="form filters-grid" @submit.prevent="applyFilters">
            <div role="group" class="field filter-field">
              <label class="label">Expansion</label>
              <select v-model.number="draftFilters.expansionId" class="select">
                <option :value="11">Midnight</option>
                <option :value="10">The War Within</option>
                <option :value="9">Dragonflight</option>
                <option :value="8">Shadowlands</option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Raid</label>
              <select v-model="draftFilters.raid" class="select">
                <option v-for="raid in draftRaids" :key="raid.slug" :value="raid.slug">
                  {{ raid.name }}
                </option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Difficulty</label>
              <select v-model="draftFilters.difficulty" class="select">
                <option value="mythic">Mythic</option>
                <option value="heroic">Heroic</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Region</label>
              <select v-model="draftFilters.region" class="select">
                <option value="world">World</option>
                <option value="eu">EU</option>
                <option value="us">US</option>
                <option value="kr">KR</option>
                <option value="tw">TW</option>
                <option value="oceanic">Oceanic</option>
              </select>
            </div>

          </form>
        </section>
        <footer>
          <button class="btn-outline" type="button" @click="closeFilters">Cancel</button>
          <button class="btn" type="button" @click="applyFilters">Apply</button>
        </footer>
      </aside>
    </div>

    <div v-if="error" class="card">
      <section>{{ error }}</section>
    </div>

    <!-- <div class="stats-grid">
      <article class="stat-card card">
        <section>
        <span>Guilds loaded</span>
        <strong>{{ filteredRankings.length }}</strong>
        </section>
      </article>
      <article class="stat-card card">
        <section>
        <span>Full clears</span>
        <strong>{{ clearedGuilds }}</strong>
        </section>
      </article>
      <article class="stat-card card">
        <section>
        <span>Total pulls</span>
        <strong>{{ totalPulls }}</strong>
        </section>
      </article>
      <article class="stat-card card">
        <section>
        <span>Latest kill</span>
        <strong>{{ latestKillLabel }}</strong>
        </section>
      </article>
    </div> -->

      <div class="raid-progress-grid">
        <aside ref="guildRankingColumn" class="guild-ranking-column">
          <header v-if="canCollapseGuildRankings" class="card ranking-section-head guild-ranking-toggle-bar">
            <div>
              <p class="eyebrow">Guild rankings</p>
              <h3>Top guilds</h3>
            </div>
            <button class="btn-icon-outline" type="button" @click="toggleGuildRankings">
              <i :class="isGuildRankingsCollapsed ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'"></i>
            </button>
          </header>

          <section v-if="isGuildRankingsCollapsed" class="card ranking-empty guild-ranking-collapsed-note">
            Guild rankings are collapsed.
          </section>

          <section v-else-if="loading" class="skeleton-stack">
            <article v-for="slot in 5" :key="`raid-skeleton-${slot}`" class="card skeleton-card">
              <div class="skeleton-row">
              <span class="skeleton-avatar"></span>
              <span class="skeleton-line skeleton-line-medium"></span>
            </div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line skeleton-line-short"></div>
          </article>
        </section>
          <section v-else-if="topGuilds.length" class="guild-ranking-list">
            <article v-for="entry in topGuilds" :key="entry.guild.id" class="card guild-rank-card">
            <div class="guild-rank-media">
              <img
                v-if="entry.guild.logo"
                :src="entry.guild.logo"
                :alt="entry.guild.displayName || entry.guild.name"
                loading="lazy"
              >
              <span v-else>{{ guildInitials(entry.guild) }}</span>
              <strong>{{ entry.rank }}</strong>
            </div>
            <div class="guild-rank-content">
              <h4>{{ entry.guild.displayName || entry.guild.name }}</h4>
              <dl>
                <div>
                  <dt>Defeat:</dt>
                  <dd>{{ defeatedCount(entry) }}/{{ selectedRaid.encounters.length }}</dd>
                </div>
                <div>
                  <dt>Region:</dt>
                  <dd>{{ entry.guild.region.name || entry.guild.region.short_name }}</dd>
                </div>
                <div>
                  <dt>Realm:</dt>
                  <dd>{{ entry.guild.realm.name }}</dd>
                </div>
              </dl>
              <a class="ranking-link" :href="guildUrl(entry.guild.path)" target="_blank" rel="noreferrer">
                <i class="fa-solid fa-angle-right"></i>
                More guild info
              </a>
            </div>
          </article>
        </section>
          <section v-else class="card ranking-empty">No rankings yet</section>

        <a class="btn guild-ranking-all" :href="leaderboardLink" target="_blank" rel="noreferrer">
          See all rankings
        </a>
      </aside>

        <section ref="bossRankingColumn" class="boss-ranking-column">
        <template v-if="loading">
          <article v-for="slot in 4" :key="`boss-skeleton-${slot}`" class="card raid-boss-rank-card raid-boss-skeleton">
            <section class="raid-boss-media">
              <span class="skeleton-media boss-skeleton-media"></span>
            </section>

            <section class="raid-boss-content">
              <header>
                <div>
                  <span class="skeleton-line skeleton-line-medium"></span>
                  <span class="skeleton-line skeleton-line-short"></span>
                </div>
                <span class="skeleton-line boss-skeleton-badge"></span>
              </header>

              <section class="boss-skeleton-table">
                <span class="skeleton-line"></span>
                <span class="skeleton-line"></span>
                <span class="skeleton-line skeleton-line-medium"></span>
              </section>

              <footer>
                <span class="skeleton-line skeleton-line-short"></span>
                <span class="skeleton-line skeleton-line-short"></span>
              </footer>
            </section>
          </article>
        </template>

        <template v-else>
        <article v-for="boss in bossRows" :key="boss.slug" class="card raid-boss-rank-card">
          <section class="raid-boss-media">
            <img
              v-if="boss.imageUrl && !failedBossImages[boss.slug]"
              :src="boss.imageUrl"
              :alt="boss.name"
              loading="lazy"
              @error="markBossImageFailed(boss.slug)"
            >
            <span v-else class="raid-boss-fallback">{{ bossInitials(boss.name) }}</span>
            <button
              v-if="boss.videoUrl"
              class="btn raid-boss-video"
              type="button"
              @click="openBossVideo(boss)"
            >
              <i class="fa-brands fa-youtube"></i>
              Video
            </button>
          </section>

          <section class="raid-boss-content">
            <header>
              <div>
                <h3>{{ boss.name }}</h3>
                <p>Total guilds: <strong>{{ boss.killCount }}</strong></p>
              </div>
              <span class="badge">{{ boss.bestPercentLabel }}</span>
            </header>

            <section class="table-wrap boss-ranking-table">
              <table class="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Faction</th>
                    <th>Realm</th>
                    <th>Region</th>
                    <th aria-label="Link"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(guild, index) in boss.guilds" :key="guild.id">
                    <td>#{{ index + 1 }}</td>
                    <td>
                      <a :href="guild.url" target="_blank" rel="noreferrer">{{ guild.name }}</a>
                    </td>
                    <td>{{ guild.faction }}</td>
                    <td>{{ guild.realm }}</td>
                    <td>{{ guild.region }}</td>
                    <td>
                      <a class="ranking-link ranking-link-icon" :href="guild.url" target="_blank" rel="noreferrer" aria-label="More info">
                        <i class="fa-solid fa-arrow-up-right-from-square"></i>
                      </a>
                    </td>
                  </tr>
                  <tr v-for="slot in emptyBossRows(boss.guilds.length)" :key="`${boss.slug}-empty-${slot}`" class="is-empty-row">
                    <td>#{{ boss.guilds.length + slot }}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <footer>
              <a class="ranking-link" :href="bossLeaderboardLink(boss.slug)" target="_blank" rel="noreferrer">
                <i class="fa-solid fa-fire-flame-curved"></i>
                See all guilds
              </a>
              <span>{{ boss.pullCount }} pulls loaded</span>
            </footer>
          </section>
        </article>
        </template>
      </section>

    </div>

    <div
      v-if="isVideoModalOpen"
      class="video-modal-backdrop"
      :class="{ 'is-closing': closingVideoModal }"
      role="presentation"
      @click="closeVideoOnBackdrop"
    >
      <div class="video-modal" role="dialog" aria-modal="true" aria-labelledby="boss-video-title" @click.stop>
        <header class="video-dialog-head">
          <div>
            <p class="eyebrow">Kill video</p>
            <h2 id="boss-video-title">{{ selectedBossVideo?.name || 'Boss kill video' }}</h2>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close" title="Close" @click="closeBossVideo">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <section class="video-player-frame">
          <iframe
            v-if="selectedBossVideoEmbedUrl"
            :src="selectedBossVideoEmbedUrl"
            :title="`${selectedBossVideo?.name || 'Boss'} kill video`"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </section>

        <footer class="video-dialog-foot">
          <a class="btn" :href="selectedBossVideo?.url" target="_blank" rel="noreferrer">
            <i class="fa-brands fa-youtube"></i>
            Open on YouTube
          </a>
        </footer>
      </div>
    </div>
  </section>
</template>

<script>
import { getRaids, getRaidHallOfFame, getRaidRankings } from '../services/raiderApi'
import { formatDate, formatNumber } from '../utils/formatters'

const BOSS_IMAGES = {
  'plexus-sentinel': 'https://wow.zamimg.com/uploads/screenshots/normal/1236194-plexus-sentinel.jpg',
  'soulbinder-naazindhri': 'https://wow.zamimg.com/uploads/screenshots/normal/1236199-soulbinder-naazindhri.jpg',
  loomithar: 'https://wow.zamimg.com/uploads/screenshots/normal/1235593-loomithar.jpg',
  'forgeweaver-araz': 'https://wow.zamimg.com/uploads/screenshots/normal/1236200-forgeweaver-araz.jpg',
  'the-soul-hunters': 'https://wow.zamimg.com/uploads/screenshots/normal/1236201-adarus-duskblaze.jpg',
  fractillus: 'https://wow.zamimg.com/uploads/screenshots/normal/1236203-fractillus.jpg',
  'nexus-king-salhadaar': 'https://wow.zamimg.com/uploads/screenshots/normal/1236205-nexus-king-salhadaar.jpg',
  dimensius: 'https://wow.zamimg.com/uploads/screenshots/normal/1235517-dimensius.jpg',
}

const DEFAULT_RAID_FILTERS = {
  expansionId: 11,
  raid: '',
  difficulty: 'mythic',
  region: 'world',
}

export default {
  name: 'RaidingView',
  data() {
    return {
      loading: false,
        isFiltersOpen: false,
      closingFilters: false,
      closingVideoModal: false,
      error: '',
      raids: [],
      rankings: [],
      hallOfFame: null,
      loadedExpansionId: null,
      failedBossImages: {},
      selectedBossVideo: null,
      isVideoModalOpen: false,
      filters: { ...DEFAULT_RAID_FILTERS },
      draftFilters: { ...DEFAULT_RAID_FILTERS },
      draftRaids: [],
      loadedDraftExpansionId: null,
      canCollapseGuildRankings: false,
      isGuildRankingsCollapsed: false,
    }
  },
  computed: {
    selectedRaid() {
      return this.raids.find((raid) => raid.slug === this.filters.raid) || { encounters: [] }
    },
    expansionLabel() {
      return this.expansionName(this.filters.expansionId)
    },
    hallBossesBySlug() {
      return (this.hallOfFame?.bossKills || []).reduce((bosses, boss) => {
        bosses[boss.boss || boss.bossSummary?.slug] = boss
        return bosses
      }, {})
    },
    filteredRankings() {
      return this.rankings
    },
    topGuilds() {
      return this.filteredRankings.slice(0, 14)
    },
    bossRows() {
      return [...this.selectedRaid.encounters].reverse().map((boss) => {
        const hallBoss = this.hallBossesBySlug[boss.slug]
        const loadedDefeatedGuilds = this.filteredRankings.filter((entry) =>
          entry.encountersDefeated?.some((defeated) => defeated.slug === boss.slug),
        )
        const guilds = (hallBoss?.defeatedBy?.guilds || [])
          .slice(0, 3)
          .map((entry) => this.hallGuildRow(entry.guild))

        const fallbackGuilds = loadedDefeatedGuilds.slice(0, 3).map((entry) => ({
          id: entry.guild.id,
          name: entry.guild.displayName || entry.guild.name,
          faction: entry.guild.faction,
          realm: entry.guild.realm.name,
          region: entry.guild.region.short_name,
          url: this.guildUrl(entry.guild.path),
        }))

        const pulls = this.filteredRankings
          .map((entry) => entry.encountersPulled?.find((item) => item.slug === boss.slug))
          .filter(Boolean)

        const defeats = this.filteredRankings
          .flatMap((entry) => entry.encountersDefeated || [])
          .filter((item) => item.slug === boss.slug)

        const bestPercent = pulls.reduce((best, item) => {
          if (item.isDefeated) {
            return best
          }
          return Math.max(best, Number(item.bestPercent || 0))
        }, 0)

        return {
          slug: boss.slug,
          name: boss.name,
          imageUrl: this.bossImageUrl(boss),
          videoUrl: this.bossVideoUrl(hallBoss) || boss.videoUrl || boss.killVideoUrl || '',
          guilds: guilds.length ? guilds : fallbackGuilds,
          killCount: hallBoss?.defeatedBy?.totalCount ?? loadedDefeatedGuilds.length,
          pullCount: pulls.reduce((total, item) => total + Number(item.numPulls || 0), 0),
          bestPercentLabel: bestPercent ? `${bestPercent.toFixed(1)}%` : 'Killed',
          firstKillLabel: formatDate(defeats[0]?.firstDefeated),
          lastKillLabel: formatDate(defeats[defeats.length - 1]?.lastDefeated),
        }
      })
    },
    clearedGuilds() {
      return this.filteredRankings.filter(
        (entry) => this.defeatedCount(entry) === this.selectedRaid.encounters.length,
      ).length
    },
    totalPulls() {
      const total = this.filteredRankings.reduce(
        (sum, entry) => sum + this.totalEntryPulls(entry, true),
        0,
      )
      return formatNumber(total)
    },
    latestKillLabel() {
      const kills = this.filteredRankings.flatMap((entry) => entry.encountersDefeated || [])
      const latest = kills
        .map((item) => item.lastDefeated)
        .filter(Boolean)
        .sort()
        .pop()
      return formatDate(latest)
    },
    leaderboardLink() {
      return `https://raider.io/${this.filters.raid}/rankings/${this.filters.region}/${this.filters.difficulty}/1`
    },
    raidIconUrl() {
      return this.selectedRaid.icon ? `https://cdn.raiderio.net/images/wow/icons/large/${this.selectedRaid.icon}.jpg` : ''
    },
    selectedBossVideoEmbedUrl() {
      const id = this.youtubeVideoId(this.selectedBossVideo?.url)
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : ''
    },
  },
  methods: {
    openFilters() {
      this.syncDraftFilters()
      this.closingFilters = false
      this.isFiltersOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeFilters() {
      if (!this.isFiltersOpen || this.closingFilters) return
      this.closingFilters = true
      window.setTimeout(() => {
        this.isFiltersOpen = false
        this.closingFilters = false
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    applyFilters() {
      this.filters = {
        ...this.filters,
        ...this.draftFilters,
      }
      this.closeFilters()
      this.loadData()
    },
    resetFilters() {
      this.filters = { ...DEFAULT_RAID_FILTERS }
      this.draftFilters = { ...DEFAULT_RAID_FILTERS }
      this.loadData()
    },
    updateGuildRankingsCollapseAvailability() {
      if (typeof window === 'undefined') return

      const wasCollapsible = this.canCollapseGuildRankings
      const canCollapse = window.matchMedia('(max-width: 1120px)').matches
      this.canCollapseGuildRankings = canCollapse

      if (!canCollapse) {
        this.isGuildRankingsCollapsed = false
        return
      }

      if (!wasCollapsible && canCollapse) {
        this.isGuildRankingsCollapsed = true
      }
    },
    toggleGuildRankings() {
      if (!this.canCollapseGuildRankings) return
      const willCollapse = !this.isGuildRankingsCollapsed
      this.isGuildRankingsCollapsed = willCollapse

      this.$nextTick(() => {
        if (willCollapse) {
          this.scrollToSection('bossRankingColumn')
          return
        }

        this.scrollToSection('guildRankingColumn')
      })
    },
    scrollToSection(refName) {
      const element = this.$refs[refName]
      if (!element?.scrollIntoView) return

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    },
    syncDraftFilters() {
      this.draftFilters = { ...this.filters }
      this.ensureDraftStaticData()
    },
    expansionName(expansionId) {
      const names = {
        11: 'Midnight',
        10: 'The War Within',
        9: 'Dragonflight',
        8: 'Shadowlands',
      }
      return names[expansionId] || `Expansion ${expansionId}`
    },
    openBossVideo(boss) {
      if (!boss.videoUrl) return

      this.selectedBossVideo = {
        name: boss.name,
        url: boss.videoUrl,
      }
      this.closingVideoModal = false
      this.isVideoModalOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeBossVideo() {
      if (!this.isVideoModalOpen || this.closingVideoModal) return
      this.closingVideoModal = true
      window.setTimeout(() => {
        this.isVideoModalOpen = false
        this.closingVideoModal = false
        this.selectedBossVideo = null
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    closeVideoOnBackdrop() {
      this.closeBossVideo()
    },
    closeVideoOnEscape(event) {
      if (event.key !== 'Escape') return

      if (this.isVideoModalOpen) {
        this.closeBossVideo()
        return
      }

      if (this.isFiltersOpen) {
        this.closeFilters()
      }
    },
    async loadData() {
      this.loading = true
      this.error = ''
      this.$store.dispatch('startGlobalLoading', 'Loading raid data...')

      try {
        await this.ensureStaticData()

        const baseParams = {
          raid: this.filters.raid,
          difficulty: this.filters.difficulty,
          region: this.filters.region,
        }

        const [rankingData, hallData] = await Promise.all([
          getRaidRankings({
            ...baseParams,
            limit: 50,
            page: 0,
          }),
          getRaidHallOfFame(baseParams),
        ])

        this.rankings = rankingData.raidRankings || []
        this.hallOfFame = hallData.hallOfFame || null
      } catch (error) {
        this.error = 'Unable to load raid progress. Check filters or try again later.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Raid load failed',
          description: this.error,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading')
      }
    },
    async ensureStaticData() {
      if (this.loadedExpansionId !== this.filters.expansionId || !this.raids.length) {
        const raidData = await getRaids(this.filters.expansionId)
        this.raids = raidData.raids || []
        this.loadedExpansionId = this.filters.expansionId
      }
      this.filters.raid = this.normalizeRaid(this.filters.raid, this.raids)
    },
    async ensureDraftStaticData() {
      if (this.loadedDraftExpansionId !== this.draftFilters.expansionId || !this.draftRaids.length) {
        const raidData = await getRaids(this.draftFilters.expansionId)
        this.draftRaids = raidData.raids || []
        this.loadedDraftExpansionId = this.draftFilters.expansionId
      }
      this.draftFilters.raid = this.normalizeRaid(this.draftFilters.raid, this.draftRaids)
    },
    normalizeRaid(currentRaid, raids) {
      if (raids.some((raid) => raid.slug === currentRaid)) {
        return currentRaid
      }
      return this.pickDefaultRaid(raids)
    },
    pickDefaultRaid(raids = this.raids) {
      const now = Date.now()
      const activeRaid = raids.find((raid) => {
        const start = new Date(raid.starts?.us || raid.starts?.eu || 0).getTime()
        const end = new Date(raid.ends?.us || raid.ends?.eu || 0).getTime()
        return start <= now && now <= end
      })

      return activeRaid?.slug || raids[raids.length - 1]?.slug || ''
    },
    guildUrl(path) {
      return `https://raider.io${path}`
    },
    guildInitials(guild) {
      return (guild.displayName || guild.name || 'G')
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
    },
    bossInitials(name) {
      return (name || 'Boss')
        .split(/\s+/)
        .filter((word) => !['the', 'of'].includes(word.toLowerCase()))
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
    },
    bossImageUrl(boss) {
      if (BOSS_IMAGES[boss.slug]) {
        return BOSS_IMAGES[boss.slug]
      }

      if (boss.icon) {
        return `https://cdn.raiderio.net/images/wow/icons/large/${boss.icon}.jpg`
      }

      return ''
    },
    markBossImageFailed(slug) {
      this.failedBossImages = {
        ...this.failedBossImages,
        [slug]: true,
      }
    },
    bossVideoUrl(hallBoss) {
      const video = hallBoss?.bossKillVideo?.find((item) => item.type === 'youtube' && item.id)
      return video ? `https://youtu.be/${video.id}` : ''
    },
    youtubeVideoId(url) {
      if (!url) return ''

      try {
        const parsedUrl = new URL(url)
        if (parsedUrl.hostname.includes('youtu.be')) {
          return parsedUrl.pathname.replace('/', '')
        }
        return parsedUrl.searchParams.get('v') || parsedUrl.pathname.split('/').pop()
      } catch (error) {
        return ''
      }
    },
    hallGuildRow(guild) {
      return {
        id: guild.id,
        name: guild.displayName || guild.name,
        faction: guild.faction,
        realm: guild.realm?.name || '-',
        region: guild.region?.short_name || '-',
        url: this.guildUrl(guild.path),
      }
    },
    bossLeaderboardLink(slug) {
      return `https://raider.io/${this.filters.raid}/boss-rankings/${slug}/${this.filters.region}/${this.filters.difficulty}`
    },
    emptyBossRows(count) {
      return Array.from({ length: Math.max(0, 3 - count) }, (_, index) => index + 1)
    },
    defeatedCount(entry) {
      return entry.encountersDefeated?.length || 0
    },
    totalEntryPulls(entry, raw = false) {
      const total = (entry.encountersPulled || []).reduce(
        (sum, item) => sum + Number(item.numPulls || 0),
        0,
      )
      return raw ? total : formatNumber(total)
    },
    bestPercent(entry) {
      const unfinished = (entry.encountersPulled || [])
        .filter((item) => !item.isDefeated)
        .sort((left, right) => left.bestPercent - right.bestPercent)[0]

      if (!unfinished) {
        return 'Full clear'
      }

      return `${unfinished.bestPercent?.toFixed(1) || '0.0'}%`
    },
    latestDefeat(entry) {
      const last = [...(entry.encountersDefeated || [])]
        .map((item) => item.lastDefeated || item.firstDefeated)
        .filter(Boolean)
        .sort()
        .pop()

      return formatDate(last)
    },
  },
  mounted() {
    this.loadData()
    this.updateGuildRankingsCollapseAvailability()
    window.addEventListener('resize', this.updateGuildRankingsCollapseAvailability)
    window.addEventListener('keydown', this.closeVideoOnEscape)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateGuildRankingsCollapseAvailability)
    window.removeEventListener('keydown', this.closeVideoOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  watch: {
    'draftFilters.expansionId'() {
      this.draftFilters.raid = ''
      this.ensureDraftStaticData()
    },
  },
}
</script>
